import i18n from 'i18next';
import { api } from './api';

export interface Notification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  notification_type: string;
  is_read: boolean;
  action_url?: string;
  sender_id?: string;
  created_at: string;
  read_at?: string;
}

export interface NotificationListResponse {
  items: Notification[];
  total: number;
  unread_count: number;
  page: number;
  size: number;
}

export const notificationService = {
  async getNotifications(params?: {
    page?: number;
    size?: number;
    notification_type?: string;
    unread_only?: boolean;
  }): Promise<NotificationListResponse> {
    try {
      const { data } = await api.get('/notifications', { params });
      return data;
    } catch (error) {
      console.error('[NotificationService] Failed to fetch notifications:', error);
      return { items: [], total: 0, unread_count: 0, page: 1, size: 20 };
    }
  },

  async getUnreadCount(): Promise<number> {
    try {
      const { data } = await api.get('/notifications/unread-count');
      return data.unread_count;
    } catch (error) {
      console.error('[NotificationService] Failed to get unread count:', error);
      return 0;
    }
  },

  async markAsRead(notificationId: string): Promise<Notification | null> {
    try {
      const { data } = await api.patch(`/notifications/${notificationId}/read`);
      return data;
    } catch (error) {
      console.error('[NotificationService] Failed to mark notification as read:', error);
      return null;
    }
  },

  async markMultipleAsRead(notificationIds: string[]): Promise<boolean> {
    try {
      await api.patch('/notifications/mark-read', { notification_ids: notificationIds });
      return true;
    } catch (error) {
      console.error('[NotificationService] Failed to mark notifications as read:', error);
      return false;
    }
  },

  async markAllAsRead(): Promise<boolean> {
    try {
      await api.patch('/notifications/mark-all-read');
      return true;
    } catch (error) {
      console.error('[NotificationService] Failed to mark all as read:', error);
      return false;
    }
  },

  formatTimeAgo(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    // "à l'instant", "il y a 5 min" in the reader's language
    const lang = (i18n.language || 'fr').slice(0, 2);
    if (minutes < 1) return lang === 'fr' ? "À l'instant" : 'Just now';
    try {
      const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto', style: 'short' });
      if (minutes < 60) return rtf.format(-minutes, 'minute');
      if (hours < 24) return rtf.format(-hours, 'hour');
      if (days < 7) return rtf.format(-days, 'day');
    } catch { /* old browser: the date below */ }
    return date.toLocaleDateString(lang);
  },
};
